import { create } from 'zustand'
import * as Sentry from '@sentry/react'
import { SecureStorage } from '../lib/telegram'
import { getUserRestrictions } from '../lib/restrictions'
import {
  UserRestrictionsSlice,
  UserRestrictionsData,
} from '../types/userRestrictions'

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes
const RESTRICTIONS_DATA_KEY = 'user_restrictions_data'
const RESTRICTIONS_TIMESTAMP_KEY = 'user_restrictions_timestamp'

// Default restricted state (fail-safe when API fails and no cache)
const defaultRestrictedState: UserRestrictionsData = {
  restrictions: {
    banking_redemption_disabled: true,
    ewallet_redemption_disabled: true,
    p2p_transfer_disabled: true,
    payment_disabled: true,
    private_key_export_disabled: true,
    banking_crud_disabled: true,
    ewallet_crud_disabled: true,
    ppob_disabled: true,
  },
}

/**
 * Load cached restrictions from SecureStorage
 */
const loadFromSecureStorage = async (): Promise<{
  data: UserRestrictionsData
  lastFetched: number | null
} | null> => {
  try {
    const dataStr = await SecureStorage.getItem(RESTRICTIONS_DATA_KEY)
    const timestampStr = await SecureStorage.getItem(RESTRICTIONS_TIMESTAMP_KEY)

    if (!dataStr) return null

    return {
      data: JSON.parse(dataStr),
      lastFetched: timestampStr ? parseInt(timestampStr) : null,
    }
  } catch {
    return null
  }
}

/**
 * Save restrictions to SecureStorage
 */
const saveToSecureStorage = async (
  data: UserRestrictionsData,
  timestamp: number
): Promise<void> => {
  try {
    await SecureStorage.setItem(RESTRICTIONS_DATA_KEY, JSON.stringify(data))
    await SecureStorage.setItem(RESTRICTIONS_TIMESTAMP_KEY, timestamp.toString())
  } catch {
    // Silently fail - caching is non-critical
  }
}

/**
 * User Restrictions Store
 *
 * Manages user restriction settings with 5-minute TTL cache stored in SecureStorage.
 * Automatically loads cached data on initialization and provides methods to fetch,
 * update, and clear restrictions.
 *
 * Security features:
 * - Stores data in Telegram SecureStorage (encrypted)
 * - Defaults to restricted state on error
 * - Falls back to cache on API failure
 */
export const useUserRestrictionsStore = create<UserRestrictionsSlice>(
  (set, get) => {
    // Auto-load from SecureStorage on initialization
    loadFromSecureStorage().then((cached) => {
      if (cached) {
        // Only set cached data if store is still in initial/empty state
        // to avoid overwriting fresher data fetched from the network
        const currentState = get()
        if (!currentState.data && !currentState.lastFetched) {
          set({
            data: cached.data,
            lastFetched: cached.lastFetched,
          })
        }
      }
    })

    return {
      // State
      data: null,
      lastFetched: null,
      isLoading: false,
      error: null,

      // Actions
      isRestrictionCacheValid: () => {
        const { lastFetched } = get()
        if (!lastFetched) return false
        return Date.now() - lastFetched < CACHE_TTL
      },

      fetchRestrictions: async (force = false) => {
        // Use cache if valid (unless forced)
        if (!force && get().isRestrictionCacheValid() && get().data) {
          return
        }

        set({ isLoading: true, error: null })

        try {
          const data = await getUserRestrictions()
          const timestamp = Date.now()

          set({
            data,
            lastFetched: timestamp,
            isLoading: false,
            error: null,
          })

          await saveToSecureStorage(data, timestamp)
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to fetch restrictions'

          // Log to Sentry for visibility into API failures
          Sentry.captureException(error, {
            tags: { feature: 'user_restrictions' },
            extra: { hasCachedData: !!get().data },
          })

          set({
            isLoading: false,
            error: errorMessage,
          })

          // Default to restricted state if no cache exists
          if (!get().data) {
            const timestamp = Date.now()
            set({
              data: defaultRestrictedState,
              lastFetched: timestamp,
            })
            await saveToSecureStorage(defaultRestrictedState, timestamp)
          }
        }
      },

      setRestrictions: async (data: UserRestrictionsData) => {
        const timestamp = Date.now()
        set({
          data,
          lastFetched: timestamp,
          error: null,
        })
        await saveToSecureStorage(data, timestamp)
      },

      clearRestrictions: async () => {
        set({
          data: null,
          lastFetched: null,
          isLoading: false,
          error: null,
        })
        await SecureStorage.removeItem(RESTRICTIONS_DATA_KEY)
        await SecureStorage.removeItem(RESTRICTIONS_TIMESTAMP_KEY)
      },

      invalidateCache: () => {
        set({ lastFetched: null })
      },
    }
  }
)
